import { useEffect, useState, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import Seo from '@/components/Seo';
import { LeadsInbox } from '@/components/admin/LeadsInbox';
import { GeocodeTool } from '@/components/admin/GeocodeTool';
import ProvidersManager from '@/components/admin/ProvidersManager';
import PropertyContactsManager from '@/components/admin/PropertyContactsManager';
import { toast } from 'sonner';
import {
  LayoutDashboard, IndianRupee, ReceiptText, CheckCircle2, AlertCircle, Users, Crown,
  Search, Download, Mail, Loader2, RefreshCw, FileText
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatINR = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

const StatCard = ({ icon: Icon, label, value, sub, accent }) => (
  <div className={`bg-white p-6 rounded-sm border border-border/50 shadow-sm ${accent || ''}`}>
    <div className="flex items-start justify-between mb-3">
      <div className="h-10 w-10 rounded-sm bg-[#0F172A]/5 flex items-center justify-center">
        <Icon className="h-5 w-5 text-[#0F172A]" />
      </div>
    </div>
    <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
    <p className="font-['Plus_Jakarta_Sans'] text-3xl font-bold text-[#0F172A]">{value}</p>
    {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
  </div>
);

const StatusPill = ({ status }) => {
  const map = {
    paid: 'bg-green-100 text-green-700',
    pending: 'bg-amber-100 text-amber-700',
    failed: 'bg-red-100 text-red-700',
    initiated: 'bg-slate-100 text-slate-700',
  };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[status] || 'bg-slate-100 text-slate-700'}`}>{status || 'unknown'}</span>;
};

const AdminDashboardPage = () => {
  const { user, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: 'all', plan: 'all', q: '' });
  const [tab, setTab] = useState('overview');
  const [newLeads, setNewLeads] = useState(0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [dashRes, txRes, invRes, leadRes] = await Promise.all([
        fetch(`${API}/admin/dashboard`, { headers }),
        fetch(`${API}/admin/transactions?limit=200&${filter.status !== 'all' ? `status=${filter.status}&` : ''}${filter.plan !== 'all' ? `plan=${filter.plan}&` : ''}${filter.q ? `q=${encodeURIComponent(filter.q)}` : ''}`, { headers }),
        fetch(`${API}/admin/invoices?limit=200`, { headers }),
        fetch(`${API}/admin/leads?limit=200`, { headers }),
      ]);

      if (leadRes.ok) setNewLeads((await leadRes.json()).new_count || 0);
      if (dashRes.ok) setStats(await dashRes.json());
      if (txRes.ok) {
        const d = await txRes.json();
        setTransactions(d.transactions || []);
      }
      if (invRes.ok) {
        const d = await invRes.json();
        setInvoices(d.invoices || []);
      }
    } catch (e) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (user?.is_admin) fetchData();
  }, [user, fetchData]);

  if (authLoading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin h-10 w-10 text-[#D4AF37]" /></div>;
  if (!user) return <Navigate to="/login?next=/admin" replace />;
  if (!user.is_admin) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F9F7]">
      <div className="bg-white p-12 rounded-sm border max-w-md text-center">
        <AlertCircle className="h-12 w-12 mx-auto text-red-500 mb-4" />
        <h1 className="font-['Plus_Jakarta_Sans'] text-3xl font-bold mb-2">Admin Only</h1>
        <p className="text-muted-foreground">This dashboard is restricted to platform administrators.</p>
      </div>
    </div>
  );

  const downloadInvoice = async (invoiceNumber) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/admin/invoices/download?invoice_number=${encodeURIComponent(invoiceNumber)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${invoiceNumber.replace(/\//g, '-')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  };

  const resendEmail = async (invoiceNumber) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/admin/invoices/resend?invoice_number=${encodeURIComponent(invoiceNumber)}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await res.json();
      if (d.sent) { toast.success('Invoice email resent'); fetchData(); }
      else toast.error('Email send failed (check Resend/domain config)');
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-16" data-testid="admin-dashboard">
      <Seo title="Admin Dashboard" description="Bharat Estate admin transactions & invoices dashboard" path="/admin" noIndex />
      <div className="container-luxury">
        <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#0F172A] text-[#D4AF37] rounded-full text-xs font-bold mb-3">
              <LayoutDashboard className="h-3.5 w-3.5" /> ADMIN
            </div>
            <h1 className="font-['Plus_Jakarta_Sans'] text-4xl font-bold text-[#0F172A]">Transactions & Revenue</h1>
            <p className="text-muted-foreground">Welcome back, {user.name}</p>
          </div>
          <Button onClick={fetchData} variant="outline" className="gap-2" data-testid="admin-refresh-btn">
            <RefreshCw className="h-4 w-4" /> Refresh
          </Button>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="admin-stats-grid">
          <StatCard icon={IndianRupee} label="Total Revenue" value={formatINR(stats?.revenue_inr_total)} sub={`${formatINR(stats?.revenue_inr_30d)} last 30d`} />
          <StatCard icon={CheckCircle2} label="Paid Transactions" value={stats?.paid_transactions ?? 0} sub={`${stats?.total_invoices ?? 0} invoices issued`} />
          <StatCard icon={AlertCircle} label="Pending / Failed" value={`${stats?.pending_transactions ?? 0} / ${stats?.failed_transactions ?? 0}`} sub="needs attention" />
          <StatCard icon={Crown} label="Premium Users" value={`${stats?.premium_users ?? 0} / ${stats?.total_users ?? 0}`} sub="conversions" />
        </div>

        {/* Plan breakdown */}
        {stats?.plan_breakdown && stats.plan_breakdown.length > 0 && (
          <div className="bg-white p-6 rounded-sm border border-border/50 mb-8">
            <h2 className="font-semibold text-[#0F172A] mb-4">Revenue by Plan</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {stats.plan_breakdown.map(p => (
                <div key={p.plan_type} className="p-4 bg-[#F8FAFC] rounded-sm">
                  <p className="text-xs text-muted-foreground capitalize">{p.plan_type}</p>
                  <p className="font-bold text-[#0F172A]">{formatINR(p.revenue)}</p>
                  <p className="text-xs text-muted-foreground">{p.count} sales</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mb-8"><GeocodeTool /></div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="bg-white">
            <TabsTrigger value="overview" data-testid="tab-transactions">Transactions ({transactions.length})</TabsTrigger>
            <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices ({invoices.length})</TabsTrigger>
            <TabsTrigger value="leads" data-testid="tab-leads">Leads{newLeads ? ` (${newLeads} new)` : ''}</TabsTrigger>
            <TabsTrigger value="providers" data-testid="tab-providers">Services Directory</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts">Seller Contacts</TabsTrigger>
          </TabsList>

          <TabsContent value="contacts" className="mt-6">
            <PropertyContactsManager />
          </TabsContent>

          <TabsContent value="providers" className="mt-6">
            <ProvidersManager />
          </TabsContent>

          <TabsContent value="leads" className="mt-6">
            <LeadsInbox onCount={setNewLeads} />
          </TabsContent>

          <TabsContent value="overview" className="mt-6">
            <div className="bg-white p-4 rounded-sm border border-border/50 mb-4 flex flex-wrap gap-3 items-end">
              <div className="flex-1 min-w-[200px]">
                <label className="text-xs text-muted-foreground">Search (email / order / payment ID)</label>
                <div className="relative mt-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Type to search..."
                    value={filter.q}
                    onChange={(e) => setFilter({ ...filter, q: e.target.value })}
                    className="pl-9"
                    data-testid="admin-search"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Status</label>
                <Select value={filter.status} onValueChange={(v) => setFilter({ ...filter, status: v })}>
                  <SelectTrigger className="w-[140px] mt-1" data-testid="admin-status-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Plan</label>
                <Select value={filter.plan} onValueChange={(v) => setFilter({ ...filter, plan: v })}>
                  <SelectTrigger className="w-[140px] mt-1" data-testid="admin-plan-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="basic">Basic</SelectItem>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                    <SelectItem value="ultimate">Ultimate</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="bg-white rounded-sm border border-border/50 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-tx-table">
                  <thead className="bg-[#F8FAFC] text-xs text-muted-foreground uppercase">
                    <tr>
                      <th className="text-left p-3">Date</th>
                      <th className="text-left p-3">User</th>
                      <th className="text-left p-3">Plan</th>
                      <th className="text-right p-3">Amount</th>
                      <th className="text-left p-3">Status</th>
                      <th className="text-left p-3">Order ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr><td colSpan={6} className="text-center py-12"><Loader2 className="animate-spin h-6 w-6 mx-auto text-[#D4AF37]" /></td></tr>
                    )}
                    {!loading && transactions.length === 0 && (
                      <tr><td colSpan={6} className="text-center py-12 text-muted-foreground">No transactions match these filters</td></tr>
                    )}
                    {transactions.map((t, i) => (
                      <tr key={t.razorpay_order_id || i} className="border-t border-border/50 hover:bg-[#F8FAFC]" data-testid={`tx-row-${i}`}>
                        <td className="p-3 whitespace-nowrap">{fmtDate(t.created_at)}</td>
                        <td className="p-3">
                          <p className="font-medium text-[#0F172A]">{t.user_email || '—'}</p>
                        </td>
                        <td className="p-3 capitalize">{t.plan_type || '—'}</td>
                        <td className="p-3 text-right font-semibold">{formatINR(t.amount)}</td>
                        <td className="p-3"><StatusPill status={t.payment_status} /></td>
                        <td className="p-3 font-mono text-xs text-muted-foreground">{t.razorpay_order_id?.slice(-12) || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="invoices" className="mt-6">
            <div className="bg-white rounded-sm border border-border/50 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-invoices-table">
                  <thead className="bg-[#F8FAFC] text-xs text-muted-foreground uppercase">
                    <tr>
                      <th className="text-left p-3">Invoice #</th>
                      <th className="text-left p-3">Customer</th>
                      <th className="text-left p-3">Plan</th>
                      <th className="text-right p-3">Total</th>
                      <th className="text-left p-3">Email</th>
                      <th className="text-left p-3">Date</th>
                      <th className="text-right p-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr><td colSpan={7} className="text-center py-12"><Loader2 className="animate-spin h-6 w-6 mx-auto text-[#D4AF37]" /></td></tr>
                    )}
                    {!loading && invoices.length === 0 && (
                      <tr><td colSpan={7} className="text-center py-12 text-muted-foreground">No invoices yet — they'll appear here after the first successful payment.</td></tr>
                    )}
                    {invoices.map((inv) => (
                      <tr key={inv.invoice_number} className="border-t border-border/50 hover:bg-[#F8FAFC]">
                        <td className="p-3 font-mono text-xs">{inv.invoice_number}</td>
                        <td className="p-3">
                          <p className="font-medium text-[#0F172A]">{inv.user_name}</p>
                          <p className="text-xs text-muted-foreground">{inv.user_email}</p>
                        </td>
                        <td className="p-3 capitalize">{inv.plan_name}</td>
                        <td className="p-3 text-right font-semibold">{formatINR(inv.total_amount)}</td>
                        <td className="p-3">
                          {inv.email_sent ? <span className="text-green-600 text-xs">✓ sent</span> : <span className="text-amber-600 text-xs">pending</span>}
                        </td>
                        <td className="p-3 whitespace-nowrap text-xs">{fmtDate(inv.created_at)}</td>
                        <td className="p-3 text-right whitespace-nowrap">
                          <Button onClick={() => downloadInvoice(inv.invoice_number)} size="sm" variant="ghost" className="h-8 w-8 p-0" title="Download" data-testid={`download-${inv.invoice_number}`}>
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button onClick={() => resendEmail(inv.invoice_number)} size="sm" variant="ghost" className="h-8 w-8 p-0" title="Resend email">
                            <Mail className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
